import {
  contractTypeLabel,
  EMPLOYEE_FUNCTION_LABELS,
  YARD_STATE_LABELS,
  type YardAccessDenial,
} from "@etudescarre/constants";
import { Badge, Button, LoadingSpinner, Modal } from "@etudescarre/ui";
import { isNil, isNotNil, type YardStatus } from "@etudescarre/utils";
import {
  ArrowRightIcon,
  BuildingsIcon,
  DownloadSimpleIcon,
  HardHatIcon,
  LockIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { authClient } from "@/lib/auth-client";
import { formatDay } from "@/lib/utils/date";
import {
  YARD_ACCESS_DENIED_TITLE,
  YARD_CLOSED_MESSAGE,
  yardAccessMessage,
} from "@/lib/yard-access";

type ContractItem = {
  employeeContractId: number;
  function: string | null;
  correspondenceMail: string | null;
  contractId: number;
  contractType: string;
  contractCode: string | null;
  isActive: boolean;
  yardId: number;
  yardName: string;
  yardType: string;
  comissioningDate: string | null;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  isLocked: boolean;
  yardStatus: YardStatus;
  isOff: boolean;
  moaCompanyName: string | null;
  performerCompanyName: string;
  referent: { name: string; email: string | null } | null;
  teams: Array<{
    id: number;
    name: string;
    members: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      isReferent: boolean;
    }>;
  }>;
  accessDenial: YardAccessDenial | null;
};

type GroupedYard = {
  yardId: number;
  yardName: string;
  yardType: string;
  comissioningDate: string | null;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  yardStatus: YardStatus;
  isOff: boolean;
  moaCompanyName: string | null;
  contracts: ContractItem[];
};

const YARD_TYPE_LABELS: Record<string, string> = {
  lots_separes: "Lots séparés",
  entreprise_generale: "Entreprise générale",
  conception_realisation: "Conception-réalisation",
};

const FUNCTION_LABELS = EMPLOYEE_FUNCTION_LABELS;

const STATUS_CONFIG: Record<
  YardStatus,
  { label: string; variant: "green" | "blue" | "yellow" | "red" }
> = {
  preparation: { label: YARD_STATE_LABELS.preparation, variant: "blue" },
  en_service: { label: YARD_STATE_LABELS.en_service, variant: "green" },
  archive: { label: YARD_STATE_LABELS.archive, variant: "yellow" },
  cloture: { label: YARD_STATE_LABELS.cloture, variant: "red" },
};

function groupByYard(contracts: ContractItem[]): GroupedYard[] {
  const map = new Map<number, GroupedYard>();

  for (const c of contracts) {
    const existing = map.get(c.yardId);
    if (existing) {
      existing.contracts.push(c);
    } else {
      map.set(c.yardId, {
        yardId: c.yardId,
        yardName: c.yardName,
        yardType: c.yardType,
        comissioningDate: c.comissioningDate,
        expirationDate: c.expirationDate,
        beforeArchivingDays: c.beforeArchivingDays,
        yardStatus: c.yardStatus,
        isOff: c.isOff,
        moaCompanyName: c.moaCompanyName,
        contracts: [c],
      });
    }
  }

  return Array.from(map.values());
}

function yardDateLines({
  comissioningDate,
  expirationDate,
  beforeArchivingDays,
}: GroupedYard): string[] {
  return [
    isNotNil(comissioningDate)
      ? `Mise en service ${formatDay(comissioningDate)}`
      : "Pas encore mis en service",
    isNotNil(expirationDate)
      ? `Expire ${formatDay(expirationDate)}`
      : "Date d'expiration non renseignée",
    isNotNil(beforeArchivingDays)
      ? `Durée archivage après expiration : ${beforeArchivingDays} ${beforeArchivingDays > 1 ? "jours" : "jour"}`
      : "Durée d'archivage non renseignée",
  ];
}

function YardStatusBadge({ group }: { group: GroupedYard }) {
  const [open, setOpen] = useState(false);
  const { yardName, yardStatus: status, isOff } = group;
  const config = STATUS_CONFIG[status];

  return (
    <span className="flex items-center gap-1.5">
      <button
        type="button"
        aria-haspopup="dialog"
        className="cursor-pointer hover:opacity-80"
        onClick={() => setOpen(true)}
        title="Voir les dates du chantier"
      >
        <Badge variant={config.variant}>{config.label}</Badge>
      </button>
      {isOff && (status === "en_service" || status === "archive") && (
        <Badge variant="red">
          <LockIcon className="mr-0.5 inline size-3" />
          OFF
        </Badge>
      )}
      <Modal.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>État du chantier - {yardName}</Modal.Title>
              </Modal.Header>
              <Modal.Body className="space-y-2 text-sm text-zinc-900 dark:text-zinc-100">
                {yardDateLines(group).map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => setOpen(false)}>Fermer</Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </span>
  );
}

function OrganisationModal({ contract }: { contract: ContractItem }) {
  const [open, setOpen] = useState(false);

  return (
    <Modal.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Button outline onClick={() => setOpen(true)} className="text-xs">
        <UsersIcon className="size-4" />
        Organisation
      </Button>
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="lg">
            <Modal.Header>
              <Modal.Title>Organisation - {contract.yardName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                    Contrat
                  </p>
                  <p className="text-zinc-900 dark:text-zinc-100">
                    {contractTypeLabel(contract.contractType)} -{" "}
                    {contract.performerCompanyName}
                    {contract.contractCode && ` (${contract.contractCode})`}
                  </p>
                </div>

                {contract.referent && (
                  <div>
                    <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
                      Référent
                    </p>
                    <p className="text-zinc-900 dark:text-zinc-100">
                      {contract.referent.name}
                      {contract.referent.email && (
                        <span className="ml-2 text-sm text-zinc-500">
                          ({contract.referent.email})
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {contract.teams.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
                      Équipes
                    </p>
                    <div className="space-y-3">
                      {contract.teams.map((team) => (
                        <div
                          key={team.id}
                          className="rounded-md border border-zinc-200 p-3 dark:border-zinc-700"
                        >
                          <p className="mb-1 font-medium text-zinc-900 dark:text-zinc-100">
                            {team.name}
                          </p>
                          {team.members.length > 0 ? (
                            <ul className="space-y-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                              {team.members.map((m) => (
                                <li key={m.id}>
                                  {[m.firstName, m.lastName]
                                    .filter(Boolean)
                                    .join(" ") || "—"}
                                  {m.isReferent && (
                                    <Badge
                                      variant="purple"
                                      className="ml-2"
                                      size="sm"
                                    >
                                      Référent
                                    </Badge>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-zinc-400 italic">
                              Aucun membre
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setOpen(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}

// Why the yard does not open, null when it does. The refusal is notified on the
// selection page itself, never on a page of its own.
function accessRefusal(contract: ContractItem): string | null {
  if (isNotNil(contract.accessDenial)) {
    return yardAccessMessage(contract.accessDenial);
  }
  return contract.yardStatus === "cloture" ? YARD_CLOSED_MESSAGE : null;
}

function ContractRow({ contract }: { contract: ContractItem }) {
  const navigate = useNavigate();
  const refusal = accessRefusal(contract);
  const isAccessible = isNil(refusal);
  const isDownloadable = isAccessible;

  const handleAccess = () => {
    if (isNil(refusal)) {
      navigate(`/yards/${contract.yardId}`);
      return;
    }
    toaster.error({ title: YARD_ACCESS_DENIED_TITLE, description: refusal });
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        <Badge variant="blue">{contractTypeLabel(contract.contractType)}</Badge>
        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {contract.performerCompanyName}
        </span>
        {contract.function && (
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            {FUNCTION_LABELS[contract.function] ?? contract.function}
          </span>
        )}
        {contract.correspondenceMail && (
          <span className="text-xs text-zinc-400 dark:text-zinc-500">
            {contract.correspondenceMail}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <OrganisationModal contract={contract} />

        <Button outline disabled={!isDownloadable} className="text-xs">
          <DownloadSimpleIcon className="size-4" />
          Téléchargements
        </Button>

        {isAccessible ? (
          <Button onClick={handleAccess} className="text-xs">
            <ArrowRightIcon className="size-4" />
            Accéder
          </Button>
        ) : (
          <Button outline onClick={handleAccess} className="text-xs">
            <LockIcon className="size-4" />
            Accéder
          </Button>
        )}
      </div>
    </div>
  );
}

function YardGroup({ group }: { group: GroupedYard }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-950">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BuildingsIcon className="size-5 text-zinc-600 dark:text-zinc-400" />
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {group.yardName}
          </h2>
          {group.moaCompanyName && (
            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              — {group.moaCompanyName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="default">
            {YARD_TYPE_LABELS[group.yardType] ?? group.yardType}
          </Badge>
          <YardStatusBadge group={group} />
        </div>
      </div>

      <div className="space-y-2">
        {group.contracts.map((c) => (
          <ContractRow key={c.employeeContractId} contract={c} />
        ))}
      </div>
    </div>
  );
}

export function OrganizationSelect() {
  const session = authClient.useSession();
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await apiClient.GET("/api/my-contracts");

    if (apiError) {
      setError("Impossible de charger vos chantiers. Veuillez réessayer.");
    } else {
      setContracts(data?.data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const groups = groupByYard(contracts);
  const user = session.data?.user;
  const userName = [user?.firstName, user?.lastName].filter(isNotNil).join(" ");

  return (
    <div className="min-h-full bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white px-4 py-4 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900/30">
              <HardHatIcon className="size-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
              Sélection de chantier
            </h1>
          </div>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            {userName || user?.email || "..."}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-5xl px-4 py-8">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <LoadingSpinner size="lg" className="text-blue-600" />
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              Chargement de vos chantiers...
            </p>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="mb-4 text-red-600 dark:text-red-400">{error}</p>
            <Button onClick={fetchContracts}>Réessayer</Button>
          </div>
        )}

        {!loading && !error && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 rounded-full bg-zinc-100 p-6 dark:bg-zinc-800">
              <BuildingsIcon className="size-12 text-zinc-400" />
            </div>
            <h2 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Vous n'avez aucun chantier actif
            </h2>
            <p className="max-w-md text-sm text-zinc-500 dark:text-zinc-400">
              Vous n'êtes rattaché à aucun contrat actif pour le moment.
              Contactez votre administrateur si vous pensez qu'il s'agit d'une
              erreur.
            </p>
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="space-y-4">
            {groups.map((g) => (
              <YardGroup key={g.yardId} group={g} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
